import { useContext, useEffect, VFC } from "react";
// Libraries
import { isEqual, isEqualWith } from "lodash";
// Utils
import { CommonContext } from "config/commonProvider";
import { useEventRequest, usePrevious, useStateSpecies } from "hooks";
// State
import { Species } from "state";
// Components
import { DropdownMenu, ImageButton, LoadingOverlay } from "components";
// Resources
import styles from "./DashboardSpeciesSelect.module.scss";

interface DashboardSpeciesSelectProps {
  species: Species[];
}

// The `species.image` property returned by S3 always has a unique `expires` GET variable in the URL
const ignoreSpeciesImageURL = (objValue, otherValue, index): boolean | undefined => {
  if (index === "image") return true;

  return;
};

export const DashboardSpeciesSelect: VFC<DashboardSpeciesSelectProps> = ({ species: speciesFromBU }) => {
  const { showNotification } = useContext(CommonContext);
  const {
    active: speciesActive,
    list: speciesList,
    resetList: resetSpeciesList,
    setActive: setSpeciesActive,
    setList: setSpeciesList,
  } = useStateSpecies();

  const previousSpeciesFromBU = usePrevious(speciesFromBU);
  useEffect(() => {
    if (!isEqualWith(previousSpeciesFromBU, speciesFromBU, ignoreSpeciesImageURL)) {
      setSpeciesList(speciesFromBU);
    }
  }, [previousSpeciesFromBU, speciesFromBU]);

  const previousSpeciesActive = usePrevious(speciesActive);
  useEffect(() => {
    if (previousSpeciesActive && speciesActive && !isEqual(previousSpeciesActive, speciesActive)) {
      showNotification({
        autoHideDuration: 2000,
        message: `Successfully switched to ${speciesActive.name}`,
      });
    }
  }, [speciesActive]);

  useEffect(
    () => () => {
      resetSpeciesList();
    },
    [],
  );

  const speciesCSS = speciesActive ? `:root { --species-colour: ${speciesActive.colour}; }` : null;

  const { eventsRequest } = useEventRequest();

  return (
    <>
      {speciesCSS && <style>{speciesCSS}</style>}

      <DropdownMenu>
        {speciesActive ? (
          <DropdownMenu.Trigger>
            <ImageButton
              backgroundColour={speciesActive.colour}
              fallbackText={speciesActive.name.charAt(0)}
              icon={speciesActive.image || undefined}
            />
          </DropdownMenu.Trigger>
        ) : (
          <LoadingOverlay customStyles={styles.dashboard_species_select__loading} />
        )}

        <DropdownMenu.Content>
          {speciesList?.map((species) => {
            const isCurrentSpecies = species.slug === speciesActive?.slug;

            const handleClickSpecies = (): void => {
              setSpeciesActive(species);
              eventsRequest({ name: "species-switch", details: { species: species.name } });
            };

            return (
              <DropdownMenu.Action
                colour={species.colour}
                icon={species.image || undefined}
                isActive={isCurrentSpecies}
                key={species.slug}
                onClick={handleClickSpecies}
                text={species.name}
              />
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu>
    </>
  );
};
